import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, X, Disc3, Volume2 } from "lucide-react";

/**
 * NowPreviewingBar — app-shell-level mini player (Spotify-style).
 *
 * Contract (events fired on `window`):
 *   bfa:preview-started   detail: { id, label, source?, color?, durationMs?, media? }
 *     · media (HTMLMediaElement, optional) → FULL player mode: real scrubber,
 *       timestamps, volume slider and pause/resume that control the element.
 *     · without media → legacy countdown pill (progress decays over durationMs).
 *   bfa:preview-ended      detail: { id? }  → bar dismisses (matches id if provided).
 *   bfa:ps-stop-all-preview                 → ALL preview sources stop, bar dismisses.
 */

function fmtTime(sec) {
  if (!sec || !isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function NowPreviewingBar() {
  const [active, setActive] = useState(null);  // {id, label, source, color, durationMs, startedAt, hasMedia}
  const [progress, setProgress] = useState(0); // 0..1 (countdown mode)
  const [curTime, setCurTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0.9);
  const mediaRef = useRef(null);
  const tickRef = useRef(null);
  const idRef = useRef(null);

  const clearTicker = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const detachMedia = () => {
    const m = mediaRef.current;
    if (m) {
      m.ontimeupdate = null;
      m.onloadedmetadata = null;
      m.ondurationchange = null;
      m.onplay = null;
      m.onpause = null;
    }
    mediaRef.current = null;
  };

  const dismiss = () => {
    clearTicker();
    detachMedia();
    setActive(null);
    setProgress(0);
    setCurTime(0);
    setDuration(0);
    idRef.current = null;
  };

  useEffect(() => {
    const onStart = (e) => {
      const d = e.detail || {};
      const id = d.id || `preview_${Date.now()}`;
      idRef.current = id;
      const startedAt = Date.now();
      const hasMedia = typeof HTMLMediaElement !== "undefined" && d.media instanceof HTMLMediaElement;
      const durationMs = Math.max(500, Math.min(60_000, d.durationMs || 8000));
      setActive({
        id, label: d.label || "Preview", source: d.source || "", color: d.color || "#4a9eff",
        durationMs, startedAt, hasMedia,
      });
      setProgress(0);
      clearTicker();
      detachMedia();

      if (hasMedia) {
        const m = d.media;
        mediaRef.current = m;
        setIsPlaying(!m.paused);
        setCurTime(m.currentTime || 0);
        setDuration(isFinite(m.duration) && m.duration > 0 ? m.duration : durationMs / 1000);
        setVolume(m.volume ?? 0.9);
        m.ontimeupdate = () => setCurTime(m.currentTime || 0);
        m.onloadedmetadata = () => { if (isFinite(m.duration) && m.duration > 0) setDuration(m.duration); };
        m.ondurationchange = () => { if (isFinite(m.duration) && m.duration > 0) setDuration(m.duration); };
        m.onplay = () => setIsPlaying(true);
        m.onpause = () => setIsPlaying(false);
      } else {
        tickRef.current = setInterval(() => {
          const elapsed = Date.now() - startedAt;
          const ratio = Math.min(1, elapsed / durationMs);
          setProgress(ratio);
          if (ratio >= 1) dismiss();
        }, 100);
      }
    };

    const onEnded = (e) => {
      const d = e.detail || {};
      if (!idRef.current) return;
      if (!d.id || d.id === idRef.current) dismiss();
    };

    const onStopAll = () => dismiss();

    window.addEventListener("bfa:preview-started", onStart);
    window.addEventListener("bfa:preview-ended", onEnded);
    window.addEventListener("bfa:ps-stop-all-preview", onStopAll);
    return () => {
      window.removeEventListener("bfa:preview-started", onStart);
      window.removeEventListener("bfa:preview-ended", onEnded);
      window.removeEventListener("bfa:ps-stop-all-preview", onStopAll);
      clearTicker();
      detachMedia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!active) return null;

  const handleStop = () => {
    try { mediaRef.current?.pause(); } catch { /* noop */ }
    try { window.dispatchEvent(new CustomEvent("bfa:ps-stop-all-preview")); } catch { /* noop */ }
    dismiss();
  };

  const togglePlay = () => {
    const m = mediaRef.current;
    if (!m) return;
    if (m.paused) m.play().catch(() => { /* noop */ });
    else m.pause();
  };

  const seekTo = (v) => {
    const m = mediaRef.current;
    const t = Number(v);
    if (m && isFinite(t)) { m.currentTime = t; setCurTime(t); }
  };

  const changeVolume = (v) => {
    const vol = Math.max(0, Math.min(1, Number(v)));
    setVolume(vol);
    if (mediaRef.current) mediaRef.current.volume = vol;
  };

  const label = active.label.length > 48 ? `${active.label.slice(0, 45)}…` : active.label;

  // ── FULL PLAYER MODE (real media element attached) ──
  if (active.hasMedia) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="now-previewing-bar"
        className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2 flex items-center gap-3 rounded-xl border border-line bg-bg-elevated/95 px-4 py-3 shadow-2xl backdrop-blur-xl w-[min(720px,92vw)]"
      >
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full shrink-0"
          style={{ background: `${active.color}22`, color: active.color }}
        >
          <Disc3 className={`h-4 w-4 ${isPlaying ? "animate-spin" : ""}`} style={{ animationDuration: "3s" }} />
        </div>
        <div className="min-w-0 w-36 sm:w-44 shrink-0">
          <div className="type-list-title text-ink-primary truncate" data-testid="now-previewing-label">{label}</div>
          {active.source && (
            <div className="type-status-badge font-mono uppercase tracking-wider text-ink-muted truncate">{active.source}</div>
          )}
        </div>
        <button
          type="button"
          onClick={togglePlay}
          data-testid="now-previewing-toggle"
          className="h-9 w-9 rounded-full bg-brand-amber text-black grid place-items-center shrink-0 hover:brightness-110 transition"
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
        <span className="font-mono text-[10px] text-ink-muted w-9 text-right shrink-0">{fmtTime(curTime)}</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={Math.min(curTime, duration || 0)}
          onChange={(e) => seekTo(e.target.value)}
          data-testid="now-previewing-seek"
          className="flex-1 h-1 accent-amber-400 cursor-pointer"
        />
        <span className="font-mono text-[10px] text-ink-muted w-9 shrink-0">{fmtTime(duration)}</span>
        <Volume2 className="h-4 w-4 text-ink-muted shrink-0 hidden sm:block" />
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={volume}
          onChange={(e) => changeVolume(e.target.value)}
          data-testid="now-previewing-volume"
          className="w-16 sm:w-20 h-1 accent-amber-400 cursor-pointer hidden sm:block"
        />
        <button
          type="button"
          onClick={handleStop}
          data-testid="now-previewing-close"
          title="Stop preview and hide"
          aria-label="Stop preview and hide"
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-ink-muted transition hover:text-ink-primary shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // ── LEGACY COUNTDOWN PILL (no media element provided) ──
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="now-previewing-bar"
      className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2 flex items-center gap-3 rounded-full border border-line bg-bg-elevated/95 px-3 py-2 shadow-2xl backdrop-blur-xl"
      style={{ minWidth: 320, maxWidth: 520 }}
    >
      <div
        className="flex h-8 w-8 items-center justify-center rounded-full"
        style={{ background: `${active.color}22`, color: active.color }}
      >
        <Disc3 className="h-4 w-4 animate-spin" style={{ animationDuration: "3s" }} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <span className="type-list-title text-ink-primary truncate">{label}</span>
          {active.source && (
            <span className="type-status-badge font-mono uppercase tracking-wider text-ink-muted shrink-0">
              {active.source}
            </span>
          )}
        </div>
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-bg">
          <div
            className="h-full transition-[width] duration-100"
            style={{ width: `${Math.round(progress * 100)}%`, background: active.color }}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={handleStop}
        data-testid="now-previewing-stop"
        title="Stop preview (Esc)"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line bg-bg text-ink-secondary transition hover:border-brand-onair hover:text-brand-onair"
      >
        <Pause className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={handleStop}
        data-testid="now-previewing-close"
        title="Stop preview and hide"
        aria-label="Stop preview and hide"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-ink-muted transition hover:text-ink-primary"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
